/**
 * IVR Journey Models
 *
 * Models for the Interactive Voice Response (IVR) Journey API
 * Represents a complete view of a caller's experience through the IVR system
 */

import type { CallTranscription } from './callTranscriptions';
import type { definitions } from '@erebus/api-types';

// ============================================================================
// Enums
// ============================================================================

export enum IVRCallStatus {
  INITIATED = 'INITIATED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export enum IVRLang {
  EN = 'EN',
  FR = 'FR',
}

export enum IVRStepType {
  START = 'START',
  END = 'END',
  MENU = 'MENU',
  ACTION = 'ACTION',
  CONDITION = 'CONDITION',
}

export enum IVRCallStepStatus {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  IN_PROGRESS = 'IN_PROGRESS',
  SKIPPED = 'SKIPPED',
}

export enum IVRActionType {
  SAY = 'SAY',
  PLAY = 'PLAY',
  GATHER = 'GATHER',
  MENU = 'MENU',
  ROUTE = 'ROUTE',
}

export enum IVRJourneyStepTrigger {
  FIRST_STEP = 'FIRST_STEP',
  SUCCESS_PATH = 'SUCCESS_PATH',
  FAILURE_PATH = 'FAILURE_PATH',
  MENU_OPTION = 'MENU_OPTION',
  DEFAULT_PATH = 'DEFAULT_PATH',
  VALIDATION_PASS = 'VALIDATION_PASS',
  VALIDATION_FAIL = 'VALIDATION_FAIL',
}

export enum IVRGatherField {
  CALL_LANG = 'CALL_LANG',
  CASE_NUMBER = 'CASE_NUMBER',
  PATIENT_FULL_NAME = 'PATIENT_FULL_NAME',
  CALLER_FULL_NAME = 'CALLER_FULL_NAME',
  PATIENT_AGE = 'PATIENT_AGE',
  PATIENT_SEX = 'PATIENT_SEX',
  CLIENT_NUMBER = 'CLIENT_NUMBER',
  TENANT_LOCATION_NAME = 'TENANT_LOCATION_NAME',
}

export enum IVRGatherInputType {
  DTMF = 'DTMF',
  SPEECH = 'SPEECH',
  /** A recorded answer with no gather row, such as the patient's name. Its value is empty. */
  RECORDING = 'RECORDING',
}

export enum IVRMenuResultType {
  OPTION_SELECTED = 'OPTION_SELECTED',
  DEFAULT_SELECTED = 'DEFAULT_SELECTED',
  INVALID_INPUT = 'INVALID_INPUT',
  TIMEOUT = 'TIMEOUT',
  MAX_ATTEMPTS = 'MAX_ATTEMPTS',
}

export enum IVRRoutingAttemptStatus {
  SUCCESS = 'SUCCESS',
  DECLINED = 'DECLINED',
  BUSY = 'BUSY',
  NO_ANSWER = 'NO_ANSWER',
  VOICEMAIL = 'VOICEMAIL',
  FAILED = 'FAILED',
}

export enum IVRRoutingResponseResult {
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
  INVALID = 'INVALID',
  NO_INPUT = 'NO_INPUT',
}

export enum IVRRoutingAttemptReason {
  BUSY = 'BUSY',
  NO_ANSWER = 'NO_ANSWER',
  VOICEMAIL = 'VOICEMAIL',
  FAX = 'FAX',
  DECLINED = 'DECLINED',
  HUNG_UP = 'HUNG_UP',
  ACCEPTED = 'ACCEPTED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
  ALREADY_BRIDGED = 'ALREADY_BRIDGED',
}

export enum IVRJourneySeverity {
  SUCCESS = 'SUCCESS',
  INFO = 'INFO',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
}

export enum IVRQualityFlagCategory {
  ROUTING = 'ROUTING',
  GATHER = 'GATHER',
  PERFORMANCE = 'PERFORMANCE',
  MENU = 'MENU',
  DATA = 'DATA',
}

export enum IVRNoteType {
  INFO = 'INFO',
  DEBUG = 'DEBUG',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  SYSTEM = 'SYSTEM',
}

// ============================================================================
// API Response Types (generated from backend OpenAPI spec via @erebus/api-types)
// ============================================================================

export type IVRCallSummaryResponse = definitions['pkg_ivrJourney.CallSummary'];
export type IVRJourneySummaryResponse = definitions['pkg_ivrJourney.Summary'];
export type JourneyOutcomeResponse = definitions['pkg_ivrJourney.Outcome'];
export type IVRStepOptionResponse = definitions['pkg_ivrJourney.StepOption'];
export type IVRJourneyStepResponse = definitions['pkg_ivrJourney.Step'];
export type IVRGatherSummaryResponse = definitions['pkg_ivrJourney.GatherSummary'];
export type IVRMenuSummaryResponse = definitions['pkg_ivrJourney.MenuSummary'];
export type IVRRoutingSummaryResponse = definitions['pkg_ivrJourney.RoutingSummary'];
export type IVRRecordingReferenceResponse = definitions['pkg_ivrJourney.RecordingReference'];
export type QualityFlagResponse = definitions['pkg_ivrJourney.QualityFlag'];
export type IVRJourneyLogNoteResponse = definitions['pkg_ivrJourney.LogNote'];
export type IVRCallJourneyResponse = definitions['pkg_ivrJourney.CallJourney'];
export type IVRJourneyResponseWrapper = definitions['pkg_ivrJourney.Response'];
export type FailoverEventResponse = definitions['pkg_ivrJourney.FailoverEvent'];

// ============================================================================
// Internal Interfaces (for use in app)
// ============================================================================

export interface IVRCallSummary {
  id: string;
  twilioCallSID: string;
  from: string;
  to: string;
  status: IVRCallStatus;
  language: IVRLang;
  flowID: string | null;
  flowName: string;
  activationID: number | null;
  startTime: Date;
  endTime: Date | null;
  durationSec: number | null;
}

export interface IVRJourneySummary {
  totalSteps: number;
  successfulSteps: number;
  failedSteps: number;
  totalGathers: number;
  successfulGathers: number;
  routingAttempts: number;
  totalRecordings: number;
}

export interface JourneyOutcome {
  code: string;
  severity: IVRJourneySeverity;
  params: Record<string, unknown>;
  description: string;
}

export interface IVRStepOption {
  label: string;
  stepID: string;
  stepName: string;
  wasTaken: boolean;
}

export interface IVRJourneyStep {
  stepID: string;
  stepName: string;
  stepType: IVRStepType;
  stepOrder: number;
  status: IVRCallStepStatus;
  timestamp: Date;
  durationSec: number | null;
  actionID: string | null;
  actionType: IVRActionType | null;
  previousStepID: string | null;
  triggerReason: IVRJourneyStepTrigger;
  triggerData: string | null;
  nextStepOptions: IVRStepOption[] | null;
  gatherResults: IVRGatherSummary[];
  menuSelections: IVRMenuSummary[];
  recordings: IVRRecordingReference[];
}

export interface IVRGatherSummary {
  fieldName: IVRGatherField;
  value: string;
  inputType: IVRGatherInputType;
  timestamp: Date;
  wasValidated: boolean;
  validationPassed: boolean | null;
  validationError: string | null;
  recordingID: string | null;
}

export interface IVRMenuSummary {
  menuID: string;
  selectedOption: number | null;
  selectedOptionLabel: string | null;
  userInput: string | null;
  inputType: IVRGatherInputType | null;
  resultType: IVRMenuResultType;
  attemptNumber: number;
  nextStepID: string | null;
  nextStepName: string | null;
  timestamp: Date;
}

/** A single answer the doctor gave to the accept/decline prompt. */
export interface IVRRoutingResponse {
  sequenceOrder: number;
  /** How the doctor answered; null when they gave no answer at all. */
  inputType: IVRGatherInputType | null;
  /** Digit pressed or words spoken; null when they gave no answer at all. */
  value: string | null;
  result: IVRRoutingResponseResult;
  timestamp: Date;
}

export interface IVRRoutingSummary {
  attemptID: string;
  sequenceOrder: number;
  doctorID: number;
  doctorName: string;
  doctorPhone: string | null;
  status: IVRRoutingAttemptStatus;
  reason: IVRRoutingAttemptReason;
  selectionReason: string;
  startTime: Date;
  endTime: Date | null;
  durationSec: number | null;
  callSID: string | null;
  /**
   * Every answer the doctor gave, in order. Empty for attempts recorded before
   * input tracking existed, and for attempts the doctor never answered.
   */
  responses: IVRRoutingResponse[];
}

export interface IVRRecordingReference {
  recordingID: string;
  fieldName: IVRGatherField;
  timestamp: Date;
  /** Machine transcript. Only tenant location name recordings have one. */
  transcript: string | null;
}

export interface QualityFlag {
  code: string;
  severity: IVRJourneySeverity;
  category: IVRQualityFlagCategory;
  params: Record<string, unknown>;
  stepID: string | null;
  detectedAt: Date;
}

export interface IVRJourneyLogNote {
  id: number;
  type: IVRNoteType;
  stepID: string | null;
  stepName: string | null;
  note: string;
  timestamp: Date;
}

export interface FailoverEvent {
  id: string;
  triggerSource: string;
  backupLine: string;
  status: string;
  errorMessage: string | null;
  timestamp: Date;
}

export interface IVRCallJourney {
  call: IVRCallSummary;
  summary: IVRJourneySummary;
  outcome: JourneyOutcome;
  qualityFlags: QualityFlag[];
  steps: IVRJourneyStep[];
  routingAttempts: IVRRoutingSummary[];
  failoverEvent: FailoverEvent | null;
  /** One transcription per doctor bridge. */
  callTranscriptions: CallTranscription[];
  logNotes: IVRJourneyLogNote[];
}

/**
 * The case context the IVR timeline views accept. Only the fields those views actually read
 * are modelled, so this stays a description of the contract rather than a second Case type.
 */
export interface IVRJourneyActivation {
  activationID?: number | null;
  isTest?: boolean;
  nonServicedReason?: string | null;
}

export interface IVRJourneyCaseContext {
  activations?: IVRJourneyActivation[];
}

// ============================================================================
// Validation Errors
// ============================================================================

export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

export class IVRJourneyValidationError extends ValidationError {
  field: string;

  constructor(message: string, field: string) {
    super(message);
    this.name = 'IVRJourneyValidationError';
    this.field = field;
  }
}

// ============================================================================
// Model Classes
// ============================================================================

/**
 * Model class for IVR Call Summary
 * Converts API response dates to Date objects
 */
export class IVRCallSummaryModel implements IVRCallSummary {
  id: string;
  twilioCallSID: string;
  from: string;
  to: string;
  status: IVRCallStatus;
  language: IVRLang;
  flowID: string | null;
  flowName: string;
  activationID: number | null;
  startTime: Date;
  endTime: Date | null;
  durationSec: number | null;

  constructor(data: IVRCallSummaryResponse) {
    this.id = data.id;
    this.twilioCallSID = data.twilioCallSID;
    this.from = data.from;
    this.to = data.to;
    this.status = data.status as IVRCallStatus;
    this.language = data.language as IVRLang;
    this.flowID = data.flowID ?? null;
    this.flowName = data.flowName;
    this.activationID = data.activationID ?? null;
    this.startTime = new Date(data.startTime);
    this.endTime = data.endTime ? new Date(data.endTime) : null;
    this.durationSec = data.durationSec ?? null;
  }
}

/**
 * Model class for IVR Journey Step
 * Converts API response dates to Date objects and validates nested options
 */
export class IVRJourneyStepModel implements IVRJourneyStep {
  stepID: string;
  stepName: string;
  stepType: IVRStepType;
  stepOrder: number;
  status: IVRCallStepStatus;
  timestamp: Date;
  durationSec: number | null;
  actionID: string | null;
  actionType: IVRActionType | null;
  previousStepID: string | null;
  triggerReason: IVRJourneyStepTrigger;
  triggerData: string | null;
  nextStepOptions: IVRStepOption[] | null;
  gatherResults: IVRGatherSummary[];
  menuSelections: IVRMenuSummary[];
  recordings: IVRRecordingReference[];

  constructor(data: IVRJourneyStepResponse) {
    this.stepID = data.stepID;
    this.stepName = data.stepName;
    this.stepType = data.stepType as IVRStepType;
    this.stepOrder = data.stepOrder;
    this.status = data.status as unknown as IVRCallStepStatus;
    this.timestamp = new Date(data.timestamp);
    this.durationSec = data.durationSec ?? null;
    this.actionID = data.actionID ?? null;
    this.actionType = (data.actionType as unknown as IVRActionType) ?? null;
    this.previousStepID = data.previousStepID ?? null;
    this.triggerReason = data.triggerReason as unknown as IVRJourneyStepTrigger;
    this.triggerData = data.triggerData ?? null;
    this.nextStepOptions = (data.nextStepOptions as IVRStepOption[]) ?? null;

    // Handle nullable nested arrays from API
    this.gatherResults =
      data.gatherResults?.map((gather) => new IVRGatherSummaryModel(gather)) || [];
    this.menuSelections = data.menuSelections?.map((menu) => new IVRMenuSummaryModel(menu)) || [];
    this.recordings =
      data.recordings?.map((recording) => new IVRRecordingReferenceModel(recording)) || [];
  }
}

/**
 * Model class for IVR Gather Summary
 * Converts API response dates to Date objects
 */
export class IVRGatherSummaryModel implements IVRGatherSummary {
  fieldName: IVRGatherField;
  value: string;
  inputType: IVRGatherInputType;
  timestamp: Date;
  wasValidated: boolean;
  validationPassed: boolean | null;
  validationError: string | null;
  recordingID: string | null;

  constructor(data: IVRGatherSummaryResponse) {
    this.fieldName = data.fieldName as IVRGatherField;
    this.value = data.value;
    this.inputType = data.inputType as IVRGatherInputType;
    this.timestamp = new Date(data.timestamp);
    this.wasValidated = data.wasValidated;
    this.validationPassed = data.validationPassed ?? null;
    this.validationError = data.validationError ?? null;
    this.recordingID = data.recordingID ?? null;
  }
}

/**
 * Model class for IVR Menu Summary
 * Converts API response dates to Date objects
 */
export class IVRMenuSummaryModel implements IVRMenuSummary {
  menuID: string;
  selectedOption: number | null;
  selectedOptionLabel: string | null;
  userInput: string | null;
  inputType: IVRGatherInputType | null;
  resultType: IVRMenuResultType;
  attemptNumber: number;
  nextStepID: string | null;
  nextStepName: string | null;
  timestamp: Date;

  constructor(data: IVRMenuSummaryResponse) {
    this.menuID = data.menuID;
    this.selectedOption = data.selectedOption ?? null;
    this.selectedOptionLabel = data.selectedOptionLabel ?? null;
    this.userInput = data.userInput ?? null;
    this.inputType = (data.inputType as IVRGatherInputType) ?? null;
    this.resultType = data.resultType as IVRMenuResultType;
    this.attemptNumber = data.attemptNumber;
    this.nextStepID = data.nextStepID ?? null;
    this.nextStepName = data.nextStepName ?? null;
    this.timestamp = new Date(data.timestamp);
  }
}

/**
 * Model class for IVR Routing Summary
 * Converts API response dates to Date objects
 */
export class IVRRoutingSummaryModel implements IVRRoutingSummary {
  attemptID: string;
  sequenceOrder: number;
  doctorID: number;
  doctorName: string;
  doctorPhone: string | null;
  status: IVRRoutingAttemptStatus;
  reason: IVRRoutingAttemptReason;
  selectionReason: string;
  startTime: Date;
  endTime: Date | null;
  durationSec: number | null;
  callSID: string | null;
  responses: IVRRoutingResponse[];

  constructor(data: IVRRoutingSummaryResponse) {
    this.attemptID = data.attemptID;
    this.sequenceOrder = data.sequenceOrder;
    this.doctorID = data.doctorID;
    this.doctorName = data.doctorName;
    this.doctorPhone = data.doctorPhone ?? null;
    this.status = data.status as unknown as IVRRoutingAttemptStatus;
    this.reason = data.reason as unknown as IVRRoutingAttemptReason;
    this.selectionReason = data.selectionReason;
    this.startTime = new Date(data.startTime);
    this.endTime = data.endTime ? new Date(data.endTime) : null;
    this.durationSec = data.durationSec ?? null;
    this.callSID = data.callSID ?? null;
    this.responses = (data.responses ?? []).map((response) => ({
      sequenceOrder: response.sequenceOrder,
      inputType: (response.inputType as IVRGatherInputType | undefined) ?? null,
      value: response.value ?? null,
      result: response.result as unknown as IVRRoutingResponseResult,
      timestamp: new Date(response.timestamp),
    }));
  }
}

/**
 * Model class for IVR Recording Reference
 * Converts API response dates to Date objects
 */
export class IVRRecordingReferenceModel implements IVRRecordingReference {
  recordingID: string;
  fieldName: IVRGatherField;
  timestamp: Date;
  transcript: string | null;

  constructor(data: IVRRecordingReferenceResponse) {
    this.recordingID = data.recordingID;
    this.fieldName = data.fieldName as IVRGatherField;
    this.timestamp = new Date(data.timestamp);
    this.transcript = data.transcript ?? null;
  }
}

/**
 * Model class for Quality Flag
 * Converts API response dates to Date objects
 */
export class QualityFlagModel implements QualityFlag {
  code: string;
  severity: IVRJourneySeverity;
  category: IVRQualityFlagCategory;
  params: Record<string, unknown>;
  stepID: string | null;
  detectedAt: Date;

  constructor(data: QualityFlagResponse) {
    this.code = data.code;
    this.severity = data.severity as IVRJourneySeverity;
    this.category = data.category as IVRQualityFlagCategory;
    this.params = data.params as Record<string, unknown>;
    this.stepID = data.stepID ?? null;
    this.detectedAt = new Date(data.detectedAt);
  }
}

/**
 * Model class for IVR Journey Log Note
 * Converts API response dates to Date objects
 */
export class IVRJourneyLogNoteModel implements IVRJourneyLogNote {
  id: number;
  type: IVRNoteType;
  stepID: string | null;
  stepName: string | null;
  note: string;
  timestamp: Date;

  constructor(data: IVRJourneyLogNoteResponse) {
    this.id = data.id;
    this.type = data.type as IVRNoteType;
    this.stepID = data.stepID ?? null;
    this.stepName = data.stepName ?? null;
    this.note = data.note;
    this.timestamp = new Date(data.timestamp);
  }
}

/**
 * Main Model class for IVR Call Journey
 * Converts all nested API response objects to their model counterparts
 */
export class FailoverEventModel implements FailoverEvent {
  id: string;
  triggerSource: string;
  backupLine: string;
  status: string;
  errorMessage: string | null;
  timestamp: Date;

  constructor(data: FailoverEventResponse) {
    this.id = data.id;
    this.triggerSource = data.triggerSource;
    this.backupLine = data.backupLine;
    this.status = data.status;
    this.errorMessage = data.errorMessage ?? null;
    this.timestamp = new Date(data.timestamp);
  }
}

/** Converts an API transcription into the domain type, with Date objects. */
function toCallTranscription(
  data: NonNullable<IVRCallJourneyResponse['callTranscriptions']>[number],
): CallTranscription {
  return {
    ...data,
    createdAt: new Date(data.createdAt!),
    updatedAt: new Date(data.updatedAt!),
  } as CallTranscription;
}

export class IVRCallJourneyModel implements IVRCallJourney {
  call: IVRCallSummary;
  summary: IVRJourneySummary;
  outcome: JourneyOutcome;
  qualityFlags: QualityFlag[];
  steps: IVRJourneyStep[];
  routingAttempts: IVRRoutingSummary[];
  failoverEvent: FailoverEvent | null;
  callTranscriptions: CallTranscription[];
  logNotes: IVRJourneyLogNote[];

  constructor(data: IVRCallJourneyResponse) {
    this.call = new IVRCallSummaryModel(data.call);
    this.summary = data.summary as IVRJourneySummary;
    this.outcome = data.outcome as JourneyOutcome;

    // Handle nullable arrays from API
    this.qualityFlags = data.qualityFlags?.map((flag) => new QualityFlagModel(flag)) || [];
    this.steps = data.steps?.map((step) => new IVRJourneyStepModel(step)) || [];
    this.routingAttempts =
      data.routingAttempts?.map((routing) => new IVRRoutingSummaryModel(routing)) || [];
    this.logNotes = data.logNotes?.map((note) => new IVRJourneyLogNoteModel(note)) || [];

    // Handle failover event with date conversion
    this.failoverEvent = data.failoverEvent ? new FailoverEventModel(data.failoverEvent) : null;

    // Handle transcriptions with date conversion
    this.callTranscriptions = (data.callTranscriptions ?? []).map(toCallTranscription);
  }
}
